/**
 * "Is this household PAYING, and which move is available?" — one module,
 * because getting it wrong stops a new user from subscribing at all.
 *
 * Since migration 0023 EVERY account has a live subscription from the moment it
 * signs up: the `free` plan, `provider: 'manual'`, `status: 'active'`, granted
 * by `billing.grant_free_plan`. Coverage no longer waits for payment — only the
 * reports surface is paid (CLAUDE.md §1). So the shapes that used to mean
 * "subscribed" no longer do:
 *
 *   subscription !== null          → true for everyone, from signup
 *   status === 'active'            → true for everyone, from signup
 *
 * Both of those shipped in the client and produced two bugs on one root cause
 * (2026-08-31): the onboarding plan step redirected every new user straight
 * past it as an "already covered" payer, and the plans screen disabled
 * Subscribe while offering "Change to" — which PATCHes and can only answer
 * `409 NO_PAID_SUBSCRIPTION`, because there is no paid subscription to change.
 *
 * `paid` is the one fact payment changes, and the read carries it for exactly
 * this. It is deliberately NOT `plan_code !== 'free'`: a plan code is a
 * catalogue entry that can be renamed or joined by another unpaid tier, and a
 * client hardcoding one is a second source of truth for entitlement.
 *
 * THIS MODULE ANSWERS ONE QUESTION ONLY: can they open a checkout? POST refuses
 * when a PAID subscription is live at the provider whatever its standing
 * (`subscribe`, src/billing/service.ts) — `past_due` is live, so a second
 * checkout is a 409, not a fix for a failed payment.
 *
 * THE OTHER QUESTION — which plans a PATCH would accept — IS NOT ANSWERED HERE.
 * The server publishes it as `changeable_to` on GET /v1/billing/subscription,
 * computed by `#planChangeRefusal`, and the screen offers exactly that list. A
 * `canChangePlan` lived here until 2026-08-31 and was deleted the moment the
 * rule grew past "paid and active": four of the twelve moves are now refused
 * outright, and a client reimplementing that would be a second source of truth
 * for a money rule — the one that disagreed would be whichever shipped last.
 */

export interface SubscriptionShape {
  plan_code: string;
  paid: boolean;
  status: string;
}

/** What the database counts as a live subscription — the status list in
 *  `billing.grant_free_plan` and every "live" query. A paid row in any of these
 *  exists at the provider, so POST would be a duplicate. */
const LIVE_STATUSES = new Set(['active', 'trialing', 'past_due', 'paused']);

/**
 * The live subscription the customer is PAYING for, or null. Null covers both
 * "nothing at all" and "the free plan" — from the client's point of view those
 * lead to the same next move, which is Subscribe.
 */
export function paidSubscription<T extends SubscriptionShape>(
  subscription: T | null | undefined,
): T | null {
  if (subscription === null || subscription === undefined) return null;
  if (!subscription.paid) return null;
  return LIVE_STATUSES.has(subscription.status) ? subscription : null;
}

/** Can this household open a Checkout session? True on the free plan — a paid
 *  checkout supersedes a free row, and the server allows it. */
export function canSubscribe(subscription: SubscriptionShape | null | undefined): boolean {
  return paidSubscription(subscription) === null;
}
